"use client";
import React, { useEffect, useState } from "react";
import type { NextPage } from "next";
import Link from "next/link";
import { useRouter } from "next/router";
import Image from "next/image";
import { ChevronDownIcon, ChevronUpIcon, HeartPulse, RadioTower } from "lucide-react";
import { usePermissions, FeatureKey } from "../../utils/usePermissions";
import { useLayout } from "../../contexts/LayoutContext";
import {
  navigationConfig,
  type NavigationSubItem,
  type SidebarSubItemLucide,
} from "./navigationConfig";
import { useAuth } from "../../contexts/AuthContext";
import TruncateTooltip from "../common/TruncateTooltip";
import { trackLogout } from "../../lib/analytics";

export type SidebarType = {
  className?: string;
  userName?: string;
  onClose?: () => void;
  isCollapsed?: boolean;
  onToggleCollapse?: () => void;
};

// Route to feature mapping for permission checks
const routeToFeature: Record<string, FeatureKey> = {
  "/ai-analyst": "ai_analyst",
  "/recon?type=pg_recon": "pg_recon",
  "/recon?type=bank_recon": "bank_recon",
  "/recon?type=ledger_recon": "ledger_recon",
  "/debugger": "debug",
  "/admin-portal": "admin_portal",
  "/admin-portal/users": "admin_portal",
  "/control-center/reminder-settings": "reminder_settings",
};

const Sidebar: NextPage<SidebarType> = ({
  className = "",
  onClose,
  userName,
  isCollapsed = false,
  onToggleCollapse,
}) => {
  const router = useRouter();
  const { isAdmin, hasPermission, findFirstAccessibleRoute } = usePermissions();
  const { showReconDashboard, setShowReconDashboard, customerName } = useLayout();
  const isHomePage = router.pathname === "/";
  const [expandedItems, setExpandedItems] = useState<Set<string>>(
    () => new Set(),
  );
  const [manuallyCollapsedParents, setManuallyCollapsedParents] = useState<
    Set<string>
  >(() => new Set());
  const [isHovered, setIsHovered] = useState(false);
  const [showLabels, setShowLabels] = useState(false);
  const showLabel = showLabels;
  const { logout } = useAuth();
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Helper function to check if a sub-item is active
  const isSubItemActive = (subItem: { href: string }) => {
    // Extract pathname and query from href (e.g., "/recon?type=pg_recon" or "/admin-portal/users")
    const [pathname, queryString] = subItem.href.split("?");
    const normalizedPath = pathname.replace(/\/$/, "") || "/";
    const currentPath = router.pathname.replace(/\/$/, "") || "/";

    // If href has a query string (e.g. recon types), match path prefix and query
    if (queryString) {
      if (!currentPath.startsWith(normalizedPath)) return false;
      const params = new URLSearchParams(queryString);
      const expectedType = params.get("type");
      if (expectedType && router.query.type !== expectedType) return false;
      return true;
    }

    // No query: require exact path match so /admin-portal doesn't match /admin-portal/users
    return currentPath === normalizedPath;
  };

  useEffect(() => {
    let timeout: NodeJS.Timeout;

    if (!isCollapsed || isHovered) {
      // expanding → wait for width animation, then show labels
      timeout = setTimeout(() => setShowLabels(true), 220);
    } else {
      // collapsing → keep labels visible until width shrink finishes
      timeout = setTimeout(() => setShowLabels(false), 220);
    }

    return () => clearTimeout(timeout);
  }, [isCollapsed, isHovered]);

  // Helper to get permitted sub-items for an item
  type SubItem = NavigationSubItem;
  const getVisibleSubItems = (subItems: SubItem[]) =>
    subItems.filter((sub) => {
      const key = sub.featureKey ?? routeToFeature[sub.href];
      return key ? hasPermission(key) : true;
    });

  // Filter navigation items based on user permissions
  const filteredNavigationConfig = navigationConfig.filter((item) => {
    // If item has direct featureKey, check permission
    if ("featureKey" in item && item.featureKey) {
      return hasPermission(item.featureKey);
    }

    // If the item has sub-items, filter those and keep the parent if any sub-items are allowed
    if ("subItems" in item && item.subItems) {
      const visible = getVisibleSubItems(item.subItems);
      return visible.length > 0;
    }

    // Default fallback
    return true;
  });
  // Flatten to display list: single recon → one direct link; multiple → parent with dropdown
  type DisplayItem = {
    text: string;
    textReconciliations?: string;
    /** Present for SVG-backed items; omitted when `lucide` is set (flattened sub-item). */
    src?: string;
    lucide?: SidebarSubItemLucide;
    href: string;
    isSubItem?: true;
    subItems?: NavigationSubItem[];
  };
  const displayItems: DisplayItem[] = [];
  for (const item of filteredNavigationConfig) {
    if (item.subItems) {
      const visible = getVisibleSubItems(item.subItems);
      if (visible.length === 1 && !("excludeFromCollapse" in item && item.excludeFromCollapse)) {
        displayItems.push({ ...visible[0], isSubItem: true });
      } else {
        displayItems.push({
          text: item.text,
          src: item.src,
          href: item.href,
          subItems: visible,
        });
      }
    } else {
      displayItems.push(item);
    }
  }

  // Keep the section for the current route expanded (add to set; don't close others)
  useEffect(() => {
    const matchedParent = filteredNavigationConfig.find((item) =>
      item.subItems?.some((subItem) => isSubItemActive(subItem)),
    );
    if (!matchedParent) return;
    if (manuallyCollapsedParents.has(matchedParent.text)) return;
    setExpandedItems((prev) => {
      if (prev.has(matchedParent.text)) return prev;
      return new Set(prev).add(matchedParent.text);
    });
    setManuallyCollapsedParents((prev) => {
      if (!prev.has(matchedParent.text)) return prev;
      const next = new Set(prev);
      next.delete(matchedParent.text);
      return next;
    });
  }, [
    router.pathname,
    router.query,
    filteredNavigationConfig,
    manuallyCollapsedParents,
  ]);

  useEffect(() => {
    if (isCollapsed) {
      setExpandedItems(new Set());
      setManuallyCollapsedParents(new Set());
    }
  }, [isCollapsed]);

  const toggleExpand = (text: string) => {
    const willBeExpanded = !expandedItems.has(text);
    setExpandedItems((prev) => {
      const next = new Set(prev);
      if (willBeExpanded) next.add(text);
      else next.delete(text);
      return next;
    });
    setManuallyCollapsedParents((prev) => {
      const next = new Set(prev);
      if (willBeExpanded) next.delete(text);
      else next.add(text);
      return next;
    });
  };

  return (
    <div
      className={`h-full border-black-10 border-r-[0.0625rem] border-solid box-border flex flex-col items-start justify-between text-left text-sm text-black-100 font-helvetica-neue px-2 ${
        isCollapsed && !isHovered ? "w-[4.5rem]" : "w-[16.5rem] "
      } transition-[width,padding,opacity] duration-500 ease-[cubic-bezier(0.4,0,0.2,1)] ${
        isCollapsed
          ? `fixed left-0 top-0 bg-white shadow-lg ${
              isHovered ? "z-[200]" : "z-50"
            }`
          : ""
      } ${className}`}
      onMouseEnter={() => isCollapsed && setIsHovered(true)}
      onMouseLeave={() => isCollapsed && setIsHovered(false)}
    >
      {/* Logo Section - Fixed at top */}
      <div
        className={`w-full flex flex-col items-start justify-start mt-[1.9rem] ml-3`}
      >
        {!isCollapsed || isHovered ? (
          <Link
            className="no-underline"
            href={isHomePage ? "#" : "/ledger-recon-dashboard"}
            passHref
            onClick={(e) => {
              if (isHomePage) return;
              const isLedgerRecon =
                router.pathname === "/recon" &&
                router.query.type === "ledger_recon";
              if (isLedgerRecon && !showReconDashboard) {
                e.preventDefault();
                setShowReconDashboard(true);
                return;
              }
              e.preventDefault();
              findFirstAccessibleRoute().then((route) => {
                if (route !== "/access-denied") router.push(route);
              });
            }}
          >
            <div className="flex flex-row items-center gap-3 justify-center ml-1">
              <Image
                className="h-7 w-auto mb-5 "
                loading="eager"
                width={150}
                height={28}
                alt="Logo"
                src="/logo.svg"
              />
              <span
                className={`
                    font-lato text-[2.2rem] whitespace-nowrap font-bold -mt-[1rem]
                    transition-all duration-300 ease-out
                    ${showLabel ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-2"}
                    text-[#01187D]
                  `}
              >
                OneCap
              </span>
            </div>
          </Link>
        ) : (
          <Link href={isHomePage ? "#" : "/ai-analyst"} passHref>
            <Image
              className="h-7 w-auto mb-4 ml-1"
              loading="eager"
              width={150}
              height={28}
              alt="Logo"
              src="/logo.svg"
            />
          </Link>
        )}
      </div>

      {/* Navigation Items - Scrollable */}
      <div className="w-full flex flex-col items-start justify-start gap-1 flex-1 overflow-y-auto scrollbar-hide">
        {displayItems.map((item) => {
          const hasDropdown =
            "subItems" in item && item.subItems && item.subItems.length > 0;
          const isActive = hasDropdown
            ? item.subItems!.some((sub) => isSubItemActive(sub))
            : item.isSubItem
              ? isSubItemActive(item)
              : router.pathname.startsWith(item.href) && item.href !== " ";

          const row = (
            <>
              {item.lucide === "HeartPulse" ? (
                <HeartPulse
                  className="h-6 w-6 ml-2 shrink-0 text-[#01187D]"
                  strokeWidth={2}
                  aria-hidden
                />
              ) : item.src ? (
                <Image
                  className={`h-6 w-6 ml-2 shrink-0`}
                  loading="eager"
                  width={24}
                  height={24}
                  alt={item.text}
                  src={item.src}
                />
              ) : null}
              {(!isCollapsed || isHovered) && (
                <>
                  <span
                    className={`
                      font-lato text-[1rem] font-normal whitespace-nowrap min-w-0
                      transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]
                      ${showLabel ? "opacity-100 translate-x-0" : "opacity-0 translate-x-2"}
                      ${isActive ? "text-black-100" : "text-gray-400"}
                    `}
                  >
                    {item.text}
                  </span>

                  {hasDropdown &&
                    (expandedItems.has(item.text) ? (
                      <ChevronUpIcon className="w-5 h-5 ml-auto mr-2" />
                    ) : (
                      <ChevronDownIcon className="w-5 h-5 ml-auto mr-2" />
                    ))}
                </>
              )}
            </>
          );

          const rowClassName = `mx-1 flex flex-row items-center py-3 px-1 min-w-0 overflow-hidden rounded-lg transition-colors duration-200 justify-start gap-4
 ${isHomePage ? "cursor-not-allowed" : "cursor-pointer"} ${
   isActive
     ? "bg-[#1C1C1C0D] text-black-100"
     : "hover:bg-black-5 text-gray-400 "
 }`;

          return (
            <div key={hasDropdown ? item.text : item.href} className="w-full">
              {hasDropdown ? (
                <div
                  className={rowClassName}
                  onClick={() => toggleExpand(item.text)}
                  title={isCollapsed ? item.text : undefined}
                >
                  {row}
                </div>
              ) : (
                <Link
                  href={isHomePage ? "#" : item.href}
                  passHref
                  className="no-underline w-full"
                >
                  <div
                    className={rowClassName}
                    title={isCollapsed ? item.text : undefined}
                  >
                    {row}
                  </div>
                </Link>
              )}

              {hasDropdown && (!isCollapsed || isHovered) && (
                <div
                  className={`
                    ml-6 mr-3 flex flex-col gap-2
                    transition-all duration-300 ease-in-out
                    ${expandedItems.has(item.text) ? "max-h-48 opacity-100 mt-2 pointer-events-auto" : "max-h-0 opacity-0 pointer-events-none"}
                  `}
                >
                  {item.subItems!.map((subItem) => (
                    <Link
                      key={subItem.href}
                      href={isHomePage ? "#" : subItem.href}
                      passHref
                      className="no-underline text-black-100 w-full"
                    >
                      <div
                        className={`w-full min-w-0 flex flex-row items-center overflow-hidden py-2 px-1 rounded-lg gap-4 transition-colors duration-200 ${
                          isHomePage ? "cursor-not-allowed" : "cursor-pointer"
                        } ${
                          isSubItemActive(subItem)
                            ? "bg-[#1C1C1C0D] text-black-100"
                            : "hover:bg-black-5 text-gray-400"
                        }`}
                      >
                        {"lucide" in subItem && subItem.lucide === "HeartPulse" ? (
                          <HeartPulse
                            className="h-6 w-6 ml-2 shrink-0 text-[#01187D]"
                            strokeWidth={2}
                            aria-hidden
                          />
                        ) : "lucide" in subItem && subItem.lucide === "RadioTower" ? (
                          <RadioTower
                            className="h-6 w-6 ml-2 shrink-0 text-[#01187D]"
                            strokeWidth={2}
                            aria-hidden
                          />
                        ) : "src" in subItem && subItem.src ? (
                          <Image
                            className="h-6 w-6 ml-2 shrink-0"
                            loading="eager"
                            width={24}
                            height={24}
                            alt={subItem.text}
                            src={subItem.src}
                          />
                        ) : null}
                        <span
                          className={`
                              font-lato text-[1rem] font-normal whitespace-nowrap min-w-0
                              transition-all duration-200 ease-out
                              ${showLabel ? "opacity-100 translate-x-0" : "opacity-0 translate-x-2"}
                              ${isSubItemActive(subItem) ? "text-black-100" : "text-gray-400"}
                            `}
                        >
                          {subItem.text}
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Sign out row (same style as other nav items) + User info below */}
        <div className="w-full mt-auto pt-3 border-t border-gray-200 flex flex-col mb-4">
          {/* Sign out - always visible, same as other sidebar items */}
          <button
            type="button"
            disabled={isLoggingOut}
            onClick={() => {
              if (isLoggingOut) return;
              setIsLoggingOut(true);
              trackLogout();
              void logout().catch(() => setIsLoggingOut(false));
            }}
            className="mx-1 w-full flex flex-row items-center py-3 px-1 min-w-0 overflow-hidden rounded-lg transition-colors duration-200 justify-start gap-3 cursor-pointer bg-transparent  text-gray-400 hover:bg-black-5 hover:text-black-100 "
            title={isCollapsed && !isHovered ? "Sign out" : undefined}
          >
            <Image
              src="/sidebar/logout.svg"
              alt="Logout"
              width={24}
              height={24}
              className="h-5 w-5 ml-2 shrink-0"
            />
            {(!isCollapsed || isHovered) && (
              <span
                className={`
                  font-lato text-[1rem] font-normal whitespace-nowrap min-w-0
                  transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]
                  ${showLabel ? "opacity-100 translate-x-0" : "opacity-0 translate-x-2"}
                 
                `}
              >
                {isLoggingOut ? "Logging out..." : "Logout"}
              </span>
            )}
          </button>

          {/* User info row */}
          {/* <div className="mx-1 flex flex-row items-center py-3 px-1 min-w-0 gap-4 border-t border-[#E0E0E0] border-solid">
            <Image
              src="/user-profile.svg"
              alt="User Avatar"
              width={24}
              height={24}
              className="h-6 w-6 ml-2 shrink-0 rounded-full"
            />
            {(!isCollapsed || isHovered) && (
              <span
                className={`
                  font-lato text-[1rem] font-normal whitespace-nowrap flex-1 min-w-0 truncate
                  transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]
                  ${showLabel ? "opacity-100 translate-x-0" : "opacity-0 translate-x-2"}
                  text-black-100
                `}
              >
                {userName}
              </span>
            )}
          </div> */}
          {/*customer name*/}
          { isAdmin && (
          <div className="mx-1 w-full flex flex-row items-center py-3  min-w-0 overflow-hidden rounded-lg transition-colors duration-200 justify-start gap-3 cursor-pointer bg-transparent text-gray-400 hover:bg-black-5 hover:text-black-100">
            <Image
              src="/sidebar/organization.svg"
              alt="Customer"
              width={24}
              height={24}
              className="h-5 w-5 ml-2 shrink-0"
            />
            {(!isCollapsed || isHovered) && (
              <span className={`font-lato text-[0.875rem] font-normal whitespace-nowrap truncate min-w-0
              transition-all duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]
              ${showLabel ? "opacity-100 translate-x-0" : "opacity-0 translate-x-2"}
              
            `}>
                <TruncateTooltip text={customerName} />  
              </span>
            )}
          </div>
          )}
        </div>
    </div>
  );
};

export default Sidebar;
